import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { Video, VideoOff, AlertTriangle, Trophy, RotateCcw, Users, Copy, Eye, Crown, Mic, MicOff, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../App";
import { 
  Card, PokerTable, DealerBadge, ResultBanner, ActionButtons, GameHeader,
  createDeck, getHandRank, compareHands 
} from "./CardPokerGames";

// ==================== MULTIPLAYER STRIP POKER ====================

const StripPoker = ({ currentUser, user, token, API: apiUrl, sendGameMessage, friends, onClose }) => {
  const { token: authToken } = useContext(AuthContext);
  const effectiveToken = token || authToken;
  const username = currentUser?.username || user?.username || "Player";
  
  // Game mode state
  const [gameMode, setGameMode] = useState("warning"); // warning, menu, create, join, playing, spectate
  const [gameCode, setGameCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  
  // Multiplayer state
  const [isHost, setIsHost] = useState(false);
  const [players, setPlayers] = useState([]);
  const [spectators, setSpectators] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const wsRef = useRef(null);
  
  // Game state
  const [deck, setDeck] = useState([]);
  const [playerHand, setPlayerHand] = useState([]);
  const [opponentHand, setOpponentHand] = useState([]);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [phase, setPhase] = useState("setup"); // setup, betting, draw, showdown, strip
  const [result, setResult] = useState(null);
  
  // Clothing/stakes state
  const [playerClothing, setPlayerClothing] = useState(5);
  const [opponentClothing, setOpponentClothing] = useState(5);
  const [showOpponentCards, setShowOpponentCards] = useState(false);
  const [playerAction, setPlayerAction] = useState(null);
  const [opponentAction, setOpponentAction] = useState(null);
  
  // Webcam state - support multiple players
  const [localStream, setLocalStream] = useState(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({}); // {peerId: stream}
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showChat, setShowChat] = useState(true);

  // Generate game code
  const generateCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "user", width: 320, height: 240 },
        audio: micEnabled 
      });
      setLocalStream(stream);
      setCameraEnabled(true);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      toast.success("Camera enabled!");
    } catch (error) {
      console.error("Camera error:", error);
      toast.error("Could not access camera");
    }
  };

  const stopCamera = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
      setCameraEnabled(false);
    }
  };

  const toggleMic = async () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMicEnabled(audioTrack.enabled);
      } else if (!micEnabled) {
        // Add audio track
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          audioStream.getAudioTracks().forEach(track => {
            localStream.addTrack(track);
          });
          setMicEnabled(true);
        } catch (e) {
          toast.error("Could not access microphone");
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [localStream]);

  // Solo game functions
  const startNewHand = useCallback(() => {
    if (playerClothing <= 0 || opponentClothing <= 0) {
      setPlayerClothing(5);
      setOpponentClothing(5);
    }
    
    const newDeck = createDeck();
    setDeck(newDeck);
    setPlayerHand([]);
    setOpponentHand([]);
    setSelectedCards(new Set());
    setPhase("betting");
    setResult(null);
    setShowOpponentCards(false);
    setPlayerAction(null);
    setOpponentAction(null);
  }, [playerClothing, opponentClothing]);

  // Create multiplayer game
  const createMultiplayerGame = () => {
    const code = generateCode();
    setGameCode(code);
    setIsHost(true);
    setPlayers([{ 
      id: username, 
      username: username, 
      isHost: true, 
      clothing: 5,
      isReady: true,
      hasCamera: false
    }]);
    setGameMode("playing");
    setPhase("waiting");
    
    // Register game for Quick Join
    axios.post(`${API}/games/register`, {
      game_type: "strip_poker",
      code: code,
      host: username,
      room_id: null,
      max_players: 4
    }, {
      headers: { Authorization: `Bearer ${effectiveToken}` }
    }).catch(console.error);
    
    toast.success(`Game created! Code: ${code}`);
  };

  // Join multiplayer game
  const joinMultiplayerGame = () => {
    if (!joinCode.trim()) {
      toast.error("Please enter a game code!");
      return;
    }
    
    setGameCode(joinCode.toUpperCase());
    setIsHost(false);
    setPlayers(prev => [...prev, { 
      id: username, 
      username: username, 
      isHost: false, 
      clothing: 5,
      isReady: false,
      hasCamera: false
    }]);
    setGameMode("playing");
    setPhase("waiting");
    toast.success(`Joined game ${joinCode.toUpperCase()}!`);
  };

  // Join as spectator
  const joinAsSpectator = () => {
    if (!joinCode.trim()) {
      toast.error("Please enter a game code!");
      return;
    }
    setGameCode(joinCode.toUpperCase());
    setGameMode("spectate");
    setSpectators(prev => [...prev, { id: username, username }]);
    toast.success(`Watching game ${joinCode.toUpperCase()} as spectator!`);
  };

  // Start the game (host only)
  const startGame = () => {
    if (!isHost) return;
    if (players.length < 2) {
      toast.error("Need at least 2 players!");
      return;
    }
    setPhase("betting");
    addChatMessage("Game started!", "system");
  };

  // Deal cards
  const deal = () => {
    const newDeck = [...deck];
    const pHand = newDeck.splice(0, 5);
    const oHand = newDeck.splice(0, 5);
    
    setDeck(newDeck);
    setPlayerHand(pHand);
    setOpponentHand(oHand);
    setPhase("draw");
  };

  // Toggle card selection
  const toggleCard = (index) => {
    if (phase !== "draw" || gameMode === "spectate") return;
    const newSelected = new Set(selectedCards);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else if (newSelected.size < 3) {
      newSelected.add(index);
    }
    setSelectedCards(newSelected);
  };

  // Draw cards
  const drawCards = (discardSet = null) => {
    const toDiscard = discardSet || selectedCards;
    const newDeck = [...deck];
    const newHand = [...playerHand];
    
    toDiscard.forEach(index => {
      newHand[index] = newDeck.shift();
    });
    
    // AI draws (for solo mode)
    const opponentNewHand = [...opponentHand];
    if (gameMode !== "playing" || players.length < 2) {
      const valueCounts = {};
      opponentHand.forEach(c => { valueCounts[c.rank] = (valueCounts[c.rank] || 0) + 1; });
      
      opponentHand.forEach((card, i) => {
        // AI keeps pairs+; discards singletons
        if (valueCounts[card.rank] < 2) {
          opponentNewHand[i] = newDeck.shift();
        }
      });
    }
    
    setDeck(newDeck);
    setPlayerHand(newHand);
    setOpponentHand(opponentNewHand);
    setSelectedCards(new Set());
    setShowOpponentCards(true);
    setPhase("showdown");
    
    setTimeout(() => {
      const comparison = compareHands(newHand, opponentNewHand);
      if (comparison > 0) {
        setResult({ type: "win", message: "Opponent removes clothing!" });
        setPlayerAction("win");
        setOpponentAction("lose");
        setOpponentClothing(prev => Math.max(0, prev - 1));
        addChatMessage(`${username} wins! Opponent strips!`, "success");
      } else if (comparison < 0) {
        setResult({ type: "lose", message: "You remove clothing!" });
        setPlayerAction("lose");
        setOpponentAction("win");
        setPlayerClothing(prev => Math.max(0, prev - 1));
        addChatMessage(`${username} loses! Time to strip!`, "error");
      } else {
        setResult({ type: "tie", message: "Tie - No one strips!" });
        addChatMessage("It's a tie! No one strips.", "info");
      }
      setPhase("strip");
    }, 1000);
  };

  // Chat functions
  const addChatMessage = (text, type = "chat") => {
    setChatMessages(prev => [...prev.slice(-19), { 
      text, 
      type, 
      user: type === "system" ? "SYSTEM" : username,
      time: new Date().toLocaleTimeString() 
    }]);
  };

  const sendChatMessage = () => {
    if (!newMessage.trim()) return;
    addChatMessage(newMessage, "chat");
    setNewMessage("");
  };

  const copyCode = () => {
    navigator.clipboard.writeText(gameCode);
    toast.success("Game code copied!");
  };

  const playerHandName = playerHand.length === 5 ? getHandRank(playerHand).name : "";
  const opponentHandName = showOpponentCards && opponentHand.length === 5 ? getHandRank(opponentHand).name : "";
  const isGameOver = playerClothing <= 0 || opponentClothing <= 0;

  // Clothing display component
  const ClothingDisplay = ({ count, label, isPlayer }) => (
    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${isPlayer ? 'bg-green-900/50' : 'bg-red-900/50'}`}>
      <span className="text-xs text-white">{label}:</span>
      <div className="flex gap-0.5">
        {Array(5).fill(0).map((_, i) => (
          <span key={i} className={`text-sm ${i < count ? '' : 'opacity-20'}`}>
            {i === 0 ? '👕' : i === 1 ? '👖' : i === 2 ? '🧦' : i === 3 ? '👟' : '🧢'}
          </span>
        ))}
      </div>
      <span className={`text-xs font-bold ${count > 2 ? 'text-green-400' : count > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
        ({count})
      </span>
    </div>
  );

  // Video feed component
  const VideoFeed = ({ stream, label, isLocal, muted = false }) => (
    <div className="relative bg-black rounded-lg overflow-hidden w-32 h-24">
      {stream ? (
        <video
          ref={isLocal ? localVideoRef : undefined}
          autoPlay
          playsInline
          muted={muted}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-500">
          <VideoOff className="w-8 h-8" />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-0.5">
        <span className="text-white text-xs truncate">{label}</span>
      </div>
    </div>
  );

  // ==================== WARNING SCREEN ====================
  if (gameMode === "warning") {
    return (
      <div className="relative w-full h-full min-h-[400px] bg-gradient-to-b from-red-900 to-pink-900 rounded-xl border-8 border-red-800 flex flex-col items-center justify-center p-8" data-testid="strip-poker-warning">
        <AlertTriangle className="w-16 h-16 text-yellow-400 mb-4" />
        <h2 className="text-2xl font-bold text-yellow-300 mb-4">⚠️ Adult Content Warning</h2>
        <div className="bg-black/40 rounded-lg p-4 max-w-md text-center mb-6">
          <p className="text-white text-sm mb-3">
            This game contains <strong>adult content</strong> and is intended for users 
            <strong> 18 years or older</strong>.
          </p>
          <p className="text-gray-300 text-xs mb-2">
            Strip Poker - Loser of each hand removes one item of clothing.
          </p>
          <p className="text-pink-300 text-xs">
            <strong>NEW:</strong> Multiplayer mode with webcam support & spectator mode!
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="px-6 py-3 bg-gray-600 hover:bg-gray-500 rounded-lg font-bold text-white">
            Exit
          </button>
          <button onClick={() => setGameMode("menu")} className="px-6 py-3 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-500 hover:to-red-500 rounded-lg font-bold text-white">
            I'm 18+ - Enter
          </button>
        </div>
      </div>
    );
  }

  // ==================== MENU SCREEN ====================
  if (gameMode === "menu") {
    return (
      <div className="w-full h-full flex flex-col bg-gradient-to-b from-red-900 to-pink-900">
        <div className="bg-gradient-to-r from-pink-900 to-red-900 px-4 py-2 flex items-center justify-between border-b border-pink-700">
          <span className="text-pink-300 font-bold text-lg">💋 STRIP POKER</span>
          <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded">18+</span>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-md space-y-4">
            {/* Solo Play */}
            <button
              onClick={() => { setGameMode("playing"); startNewHand(); }}
              className="w-full py-4 bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-500 hover:to-pink-600 rounded-lg text-white font-bold text-lg shadow-lg"
            >
              🎮 SOLO vs AI
            </button>
            
            {/* Create Multiplayer */}
            <button
              onClick={() => setGameMode("create")}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 rounded-lg text-white font-bold text-lg shadow-lg"
            >
              <div className="flex items-center justify-center gap-2">
                <Crown className="w-5 h-5" />
                CREATE MULTIPLAYER GAME
              </div>
              <div className="text-xs opacity-75 mt-1">With webcam support!</div>
            </button>
            
            {/* Join Game */}
            <button
              onClick={() => setGameMode("join")}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-lg text-white font-bold text-lg shadow-lg"
            >
              <div className="flex items-center justify-center gap-2">
                <Users className="w-5 h-5" />
                JOIN GAME
              </div>
            </button>
            
            <div className="text-center text-pink-200 text-sm mt-4">
              Play against AI or invite friends for multiplayer fun!
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== CREATE GAME SCREEN ====================
  if (gameMode === "create") {
    return (
      <div className="w-full h-full flex flex-col bg-gradient-to-b from-red-900 to-pink-900">
        <div className="bg-gradient-to-r from-pink-900 to-red-900 px-4 py-2 flex items-center justify-between border-b border-pink-700">
          <span className="text-pink-300 font-bold">💋 CREATE GAME</span>
          <button onClick={() => setGameMode("menu")} className="text-gray-400 hover:text-white">← Back</button>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-md bg-black/30 rounded-xl p-6 space-y-4">
            <div className="text-center mb-4">
              <Video className="w-12 h-12 text-pink-400 mx-auto mb-2" />
              <h3 className="text-white font-bold text-lg">Multiplayer Strip Poker</h3>
              <p className="text-pink-200 text-sm">Up to 4 players with webcam support</p>
            </div>
            
            <div className="bg-pink-900/50 rounded-lg p-3 text-sm text-pink-200">
              <strong>Features:</strong>
              <ul className="list-disc list-inside mt-1 text-xs space-y-1">
                <li>Real-time video chat with all players</li>
                <li>Spectator mode for watchers</li>
                <li>Private game codes</li>
                <li>Turn-based gameplay</li>
              </ul>
            </div>
            
            <button
              onClick={createMultiplayerGame}
              className="w-full py-4 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-500 hover:to-red-500 rounded-lg text-white font-bold text-lg"
            >
              CREATE PRIVATE GAME
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== JOIN GAME SCREEN ====================
  if (gameMode === "join") {
    return (
      <div className="w-full h-full flex flex-col bg-gradient-to-b from-red-900 to-pink-900">
        <div className="bg-gradient-to-r from-pink-900 to-red-900 px-4 py-2 flex items-center justify-between border-b border-pink-700">
          <span className="text-pink-300 font-bold">💋 JOIN GAME</span>
          <button onClick={() => setGameMode("menu")} className="text-gray-400 hover:text-white">← Back</button>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-md bg-black/30 rounded-xl p-6 space-y-4">
            <div>
              <label className="text-pink-200 text-sm font-bold block mb-2">Game Code</label>
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="Enter 6-character code"
                className="w-full p-3 bg-pink-900/50 text-white rounded-lg border border-pink-600 focus:border-pink-400 outline-none text-center text-xl font-mono tracking-widest"
                maxLength={6}
              />
            </div>
            
            <button
              onClick={joinMultiplayerGame}
              disabled={joinCode.length !== 6}
              className="w-full py-4 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-500 hover:to-red-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-bold text-lg"
            >
              <Users className="w-5 h-5 inline mr-2" />
              JOIN AS PLAYER
            </button>
            
            <button
              onClick={joinAsSpectator}
              disabled={joinCode.length !== 6}
              className="w-full py-3 bg-purple-700 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-bold"
            >
              <Eye className="w-5 h-5 inline mr-2" />
              WATCH AS SPECTATOR
            </button>
            
            <p className="text-pink-300 text-xs text-center">
              Spectators can watch but cannot participate
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ==================== GAME OVER SCREEN ====================
  if (isGameOver && gameMode !== "spectate") {
    const playerWon = opponentClothing <= 0;
    return (
      <div className="relative w-full h-full min-h-[400px] bg-gradient-to-b from-red-900 to-pink-900 rounded-xl border-8 border-red-800 flex flex-col items-center justify-center p-8" data-testid="strip-poker-gameover">
        <Trophy className={`w-20 h-20 ${playerWon ? 'text-yellow-400' : 'text-gray-400'} mb-4`} />
        <h2 className={`text-3xl font-bold mb-4 ${playerWon ? 'text-yellow-300' : 'text-red-300'}`}>
          {playerWon ? "🎉 YOU WON!" : "😳 YOU LOST!"}
        </h2>
        <p className="text-white text-lg mb-6">
          {playerWon ? "Your opponent has nothing left!" : "You've got nothing left!"}
        </p>
        <button
          onClick={() => { setPlayerClothing(5); setOpponentClothing(5); startNewHand(); }}
          className="px-8 py-3 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-500 hover:to-red-500 rounded-lg font-bold text-white text-lg"
        >
          Play Again
        </button>
      </div>
    );
  }

  // ==================== MAIN GAME / SPECTATE VIEW ====================
  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-900 to-red-900 px-4 py-2 flex items-center justify-between border-b border-pink-700">
        <div className="flex items-center gap-3">
          <span className="text-pink-300 font-bold">💋 STRIP POKER</span>
          <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded">18+</span>
          {gameCode && (
            <div className="flex items-center gap-1">
              <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded font-mono">{gameCode}</span>
              <button onClick={copyCode} className="text-gray-400 hover:text-white">
                <Copy className="w-3 h-3" />
              </button>
            </div>
          )}
          {gameMode === "spectate" && (
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
              <Eye className="w-3 h-3" /> SPECTATING
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ClothingDisplay count={opponentClothing} label="Opp" isPlayer={false} />
          <ClothingDisplay count={playerClothing} label="You" isPlayer={true} />
          {gameMode !== "spectate" && (
            <>
              <button 
                onClick={cameraEnabled ? stopCamera : startCamera}
                className={`p-1.5 rounded ${cameraEnabled ? 'bg-green-600' : 'bg-gray-600'} text-white`}
                title={cameraEnabled ? "Stop Camera" : "Start Camera"}
              >
                {cameraEnabled ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
              </button>
              {cameraEnabled && (
                <button 
                  onClick={toggleMic}
                  className={`p-1.5 rounded ${micEnabled ? 'bg-green-600' : 'bg-gray-600'} text-white`}
                  title={micEnabled ? "Mute" : "Unmute"}
                >
                  {micEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                </button>
              )}
            </>
          )}
          <button 
            onClick={() => setShowChat(!showChat)}
            className={`p-1.5 rounded ${showChat ? 'bg-purple-600' : 'bg-gray-600'} text-white`}
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          <button onClick={startNewHand} className="p-1.5 bg-blue-600 hover:bg-blue-500 rounded text-white">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Video Feeds (if multiplayer) */}
        {(gameMode === "playing" || gameMode === "spectate") && players.length > 0 && (
          <div className="w-36 bg-gray-900 border-r border-gray-700 p-2 space-y-2 overflow-y-auto">
            <div className="text-gray-400 text-xs font-bold mb-1">
              <Users className="w-3 h-3 inline mr-1" />
              PLAYERS ({players.length})
            </div>
            
            {/* Local video */}
            {cameraEnabled && (
              <VideoFeed stream={localStream} label={`${username} (You)`} isLocal={true} muted={true} />
            )}
            
            {/* Remote videos placeholder */}
            {players.filter(p => p.username !== username).map(player => (
              <div key={player.id} className="relative bg-black rounded-lg overflow-hidden w-full h-24">
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  {player.hasCamera ? (
                    <Video className="w-8 h-8 text-green-500" />
                  ) : (
                    <VideoOff className="w-8 h-8" />
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-0.5 flex items-center justify-between">
                  <span className="text-white text-xs truncate">{player.username}</span>
                  {player.isHost && <Crown className="w-3 h-3 text-yellow-400" />}
                </div>
                <div className="absolute top-1 right-1">
                  <span className="text-xs">
                    {Array(player.clothing).fill('👕').join('')}
                  </span>
                </div>
              </div>
            ))}
            
            {/* Spectators */}
            {spectators.length > 0 && (
              <>
                <div className="text-gray-400 text-xs font-bold mt-2">
                  <Eye className="w-3 h-3 inline mr-1" />
                  SPECTATORS ({spectators.length})
                </div>
                {spectators.map(spec => (
                  <div key={spec.id} className="text-xs text-gray-400 px-2">
                    {spec.username}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* TABLE AREA */}
        <div className="flex-1 relative">
          {/* Waiting for players (multiplayer) */}
          {phase === "waiting" && gameCode && (
            <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-20">
              <div className="bg-gray-800 rounded-xl p-6 text-center max-w-sm">
                <div className="text-pink-400 font-bold text-xl mb-2">Waiting for Players</div>
                <div className="text-gray-400 text-sm mb-4">Share this code with friends:</div>
                <div className="bg-gray-900 px-6 py-3 rounded-lg font-mono text-2xl text-white mb-4">{gameCode}</div>
                <button onClick={copyCode} className="text-blue-400 hover:text-blue-300 text-sm mb-4 flex items-center gap-1 mx-auto">
                  <Copy className="w-4 h-4" /> Copy Code
                </button>
                <div className="text-gray-500 text-xs mb-4">
                  {players.length} player(s) joined
                </div>
                {isHost && players.length >= 2 && (
                  <button
                    onClick={startGame}
                    className="w-full py-3 bg-pink-600 hover:bg-pink-500 rounded-lg text-white font-bold"
                  >
                    START GAME
                  </button>
                )}
              </div>
            </div>
          )}
          
          <PokerTable>
            <DealerBadge phase={phase} gameName="STRIP POKER" />
            
            {/* Opponent */}
            <div className="absolute top-16 left-1/2 -translate-x-1/2">
              <div className="flex flex-col items-center">
                {opponentAction && (
                  <div className={`px-2 py-0.5 rounded text-[10px] font-bold text-white mb-1 ${
                    opponentAction === 'win' ? 'bg-green-500' : 'bg-red-500'
                  }`}>
                    {opponentAction.toUpperCase()}
                  </div>
                )}
                
                <div className="w-12 h-12 rounded-full ring-2 ring-pink-400 overflow-hidden bg-gradient-to-br from-pink-600 to-red-800 flex items-center justify-center">
                  <span className="text-xl">👩</span>
                </div>
                <div className="text-xs font-bold text-pink-300 mt-1">
                  {players.length > 1 ? players.find(p => !p.isHost)?.username || "Opponent" : "AI Opponent"}
                </div>
                
                <div className="flex gap-1 mt-2">
                  {opponentHand.map((card, i) => (
                    <Card key={i} card={showOpponentCards ? card : null} faceDown={!showOpponentCards} size="normal" />
                  ))}
                </div>
                {opponentHandName && <div className="text-yellow-300 text-xs mt-1">{opponentHandName}</div>}
              </div>
            </div>
            
            {/* Stakes */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-pink-600/50 rounded-full px-4 py-2">
                <span className="text-white font-bold text-sm">Stakes: 1 Item</span>
              </div>
            </div>
            
            <ResultBanner result={result?.type} message={result?.message} />
            
            {/* Player */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
              <div className="flex flex-col items-center">
                {playerAction && (
                  <div className={`px-2 py-0.5 rounded text-[10px] font-bold text-white mb-1 ${
                    playerAction === 'win' ? 'bg-green-500' : 'bg-red-500'
                  }`}>
                    {playerAction.toUpperCase()}
                  </div>
                )}
                
                <div className="flex gap-1">
                  {playerHand.map((card, i) => (
                    <Card 
                      key={i} 
                      card={card} 
                      size="large" 
                      selected={selectedCards.has(i)}
                      onClick={() => toggleCard(i)}
                    />
                  ))}
                </div>
                {playerHandName && <div className="text-green-300 text-xs mt-1">{playerHandName}</div>}
                {phase === "draw" && gameMode !== "spectate" && (
                  <div className="text-xs text-gray-300 mt-1">Click cards to discard (max 3)</div>
                )}
              </div>
            </div>
            
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
              <div className="text-xs font-bold text-green-400">★ {username} ★</div>
            </div>
          </PokerTable>
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="w-48 bg-gray-900 border-l border-gray-700 flex flex-col">
            <div className="bg-gray-800 px-2 py-1 text-xs font-bold text-gray-400 flex items-center justify-between">
              <span>CHAT</span>
              <button onClick={() => setShowChat(false)} className="hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1 text-xs">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`${
                  msg.type === 'system' ? 'text-yellow-400 italic' :
                  msg.type === 'success' ? 'text-green-400' :
                  msg.type === 'error' ? 'text-red-400' : 'text-gray-300'
                }`}>
                  {msg.type !== 'system' && <span className="text-pink-400 font-bold">{msg.user}: </span>}
                  {msg.text}
                </div>
              ))}
            </div>
            {gameMode !== "spectate" && (
              <div className="p-1 border-t border-gray-700">
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                    placeholder="Type..."
                    className="flex-1 bg-gray-800 text-white text-xs px-2 py-1 rounded outline-none"
                  />
                  <button onClick={sendChatMessage} className="bg-pink-600 text-white px-2 rounded">
                    ↵
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* CONTROLS */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-3 border-t border-gray-700">
        {gameMode === "spectate" ? (
          <div className="text-center text-gray-400 py-2">
            <Eye className="w-5 h-5 inline mr-2" />
            You are spectating - enjoy the show!
          </div>
        ) : (
          <ActionButtons 
            disabled={false}
            actions={
              phase === "betting" ? [
                { label: "Deal Cards", color: "green", onClick: deal }
              ] :
              phase === "draw" ? [
                { label: `Draw ${selectedCards.size}`, color: "yellow", onClick: () => drawCards() },
                { label: "Stand Pat", color: "blue", onClick: () => { setSelectedCards(new Set()); drawCards(new Set()); } }
              ] :
              [
                { label: "Next Hand", color: "green", onClick: startNewHand }
              ]
            }
          />
        )}
      </div>
    </div>
  );
};

export default StripPoker;
